import React from 'react';
import PropTypes from 'prop-types';
import { Sentry } from '@/services/errorTracking';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary component that catches React render errors
 * and reports them to Sentry while showing a friendly fallback UI
 */
class ErrorBoundaryClass extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary] Caught error:', error, errorInfo);

        // Report to Sentry with component stack
        if (process.env.REACT_APP_SENTRY_DSN) {
            Sentry.withScope((scope) => {
                scope.setExtra('componentStack', errorInfo.componentStack);
                Sentry.captureException(error);
            });
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6">
                    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                        <div className="flex justify-center mb-4">
                            <div className="bg-red-100 rounded-full p-3">
                                <AlertTriangle className="h-8 w-8 text-red-600" />
                            </div>
                        </div>

                        <h1 className="text-xl font-semibold text-slate-900 mb-2">
                            Something went wrong
                        </h1>

                        <p className="text-slate-600 mb-6">
                            We&apos;ve been notified and are working to fix the issue.
                            Please try refreshing the page.
                        </p>

                        <div className="flex flex-col gap-3">
                            <Button
                                onClick={this.handleRetry}
                                className="w-full"
                            >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh Page
                            </Button>

                            <Button
                                variant="outline"
                                onClick={() => window.location.href = '/'}
                                className="w-full"
                            >
                                Go to Home
                            </Button>
                        </div>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="text-sm text-slate-500 cursor-pointer">
                                    Error details (dev only)
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-100 rounded text-xs overflow-auto max-h-40">
                                    {this.state.error.toString()}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Export the class directly as the ErrorBoundary
// We don't wrap with Sentry.ErrorBoundary as it causes double error reporting
// and the class already reports to Sentry in componentDidCatch
export const ErrorBoundary = ErrorBoundaryClass;

export default ErrorBoundary;

ErrorBoundaryClass.propTypes = {
    children: PropTypes.node
};
